<script setup>
import { storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useClientStore } from '@/stores/client';
import { useOptionsStore } from '@/stores/options';
import { useUserStore } from '@/stores/user';
import { router } from '@inertiajs/vue3';

const { searchClientSupportPlan } = useClientStore();
const { clientSupportPlans } = storeToRefs(useClientStore());
const { supportPlanStatusOptions, supportPlanTypeOptions } = storeToRefs(useOptionsStore());
const { profile } = storeToRefs(useUserStore());

import { useSupportPlanEventStore } from '@/stores/supportPlanEvent';
const { createBlankEvent } = useSupportPlanEventStore();

import { inject } from 'vue';
const modals = inject('modals');

import { isRole } from '@/utils/roleHelper';
const roles = computed(() => isRole(profile.value));

const props = defineProps({
    client: {
        type: Object,
        default: () => null
    }
});

const params = ref({
    term: '',
    page: 1,
    per_page: 10,
    status: null, 
    type: null, 
});

const fetchSupportPlans = () => {
    searchClientSupportPlan({
        ...params.value,
        hash: props.client.hash,
    });
};

const openSupportPlanModal = (supportPlan) => {
    modals.show('SupportPlanModal', {
        props: {
            mode: 'Create',
            plan: supportPlan,
            client: props.client,
        }
    }).then(() => {
        fetchSupportPlans();
    });
};

const generateSupportPlanReport = (supportPlan) => {
    window.open(`/api/v1/clients/${props.client.hash}/support-plan/${supportPlan.hash}/report`, '_blank');
};

const createNewEvent = async (supportPlan) => {
    const payload = {
        support_plan_id: supportPlan.id,
    };
    const resp = await createBlankEvent(payload);

    const event = resp.data.event;

    window.location.href = `/clients/${props.client.hash}/support-plan/${supportPlan.hash}/events/${event.hash}`;
};

const accessSupportPlan = (supportPlan) => {
    router.visit(`/clients/${props.client.hash}/support-plan/${supportPlan.hash}`);
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    fetchSupportPlans();
};

const allowAddNewPlan = computed(() => {
    if (roles.value.clientSupportWorker()) {
        return false;
    }
    return true;
});

</script>

<template>
<div>
    <div v-if="allowAddNewPlan" class="flex justify-end pb-4">
        <StyledButton @click="openSupportPlanModal(null)">
            <FontAwesomeIcon icon="plus" class="mr-1" />{{ $t('label.new_support_plan') }}
        </StyledButton>
    </div>
    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledDropdown
            v-model="params.status"
            clearable
            :placeholder="$t('label.status')"
            :options="supportPlanStatusOptions"
            name="status"
            :label="$t('label.status')" 
            @input="fetchSupportPlans" />
        <StyledDropdown
            v-model="params.type"
            :placeholder="$t('label.type')"
            :options="supportPlanTypeOptions"
            name="type"
            :label="$t('label.type')" 
            @input="fetchSupportPlans" />
    </div>
    
    <DataTable
        v-if="clientSupportPlans && clientSupportPlans.data"
        :data="clientSupportPlans"
        :headers="[
            { title: $t('label.start_date'), key: 'start_date', value: item => `${item.start_date.split('T')[0]}` },
            { title: $t('label.review_date'), key: 'review_date', value: item => `${item.review_date.split('T')[0]}` },
            { title: $t('label.status'), key: 'status', value: item => `${item.status.name}` },
            { title: $t('label.type'), key: 'type', value: item => `${item.type.name}` },
        ]"
        :actions="allowAddNewPlan
            ? [
                {
                    title: $t('label.access'),
                    icon: 'mdi-arrow-right-circle-outline',
                    action: (item) => accessSupportPlan(item),
                },
                {
                    title: $t('label.track_new_event'),
                    icon: 'mdi-calendar-plus',
                    action: (item) => createNewEvent(item),
                },
                {
                    title: $t('label.generate_support_plan_report'),
                    icon: 'mdi-file-outline',
                    action: (item) => generateSupportPlanReport(item),
                }
            ]
            : [
                {
                    title: $t('label.track_new_event'),
                    icon: 'mdi-calendar-plus',
                    action: (item) => createNewEvent(item),
                }
            ]
        " 
        @table-update="tableUpdate" />
    <div v-else>
        {{ $t('label.no_support_plans') }}
    </div>
</div>
</template>
